import React from 'react';
import Logo from './ui/Logo';
import { Button } from './ui/button';
import { ChevronDown, ChevronLeft, ChevronRight, LogOut, User } from 'lucide-react';

const Sidebar = ({ menuItems, activeTab, activeSubTab, expandedMenus, sidebarCollapsed, user, setActiveTab, setActiveSubTab, toggleMenu, setSidebarCollapsed, logout, setShowNewTicketDialog }) => {
  return (
    <div className={`brand-sidebar text-white shadow-2xl transition-all duration-300 flex flex-col h-screen fixed left-0 top-0 z-50 border-r border-white/10 ${sidebarCollapsed ? 'w-16' : 'w-72'}`}>
      <div className="h-16 flex items-center justify-between px-4 border-b border-white/10 flex-shrink-0">
        {sidebarCollapsed ? (
          <Logo variant="icon" iconClassName="h-8 w-auto mx-auto" />
        ) : (
          <Logo variant="full" theme="dark" iconClassName="h-8 w-auto" wordClassName="text-lg" />
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden md:inline-flex text-white/60 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors"
        >
          {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent hover:scrollbar-thumb-white/20 min-h-0">
        <nav className="space-y-1 px-3">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;

            if (item.submenus) {
              const isExpanded = expandedMenus[item.id] !== false;
              return (
                <div key={item.id}>
                  <div className="flex items-center">
                    <button
                      onClick={() => {
                        setActiveTab(item.id);
                        if (!sidebarCollapsed) {
                          if (activeTab !== item.id) {
                            setActiveSubTab(item.submenus[0].id);
                            toggleMenu(item.id);
                          }
                        }
                      }}
                      className={`flex-1 group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${isActive ? 'bg-white/10 text-white shadow-lg backdrop-blur-sm ring-1 ring-white/10' : 'text-white/60 hover:bg-white/10 hover:text-white hover:shadow-md'}`}
                      title={item.name}
                    >
                      <Icon className={`h-5 w-5 ${sidebarCollapsed ? '' : 'mr-3'} transition-transform group-hover:scale-110`} />
                      {!sidebarCollapsed && (
                        <span className="truncate flex-1 text-left">{item.name}</span>
                      )}
                    </button>
                    {!sidebarCollapsed && (
                      <button
                        onClick={() => toggleMenu(item.id)}
                        className="p-2 text-white/40 hover:text-white transition-colors"
                        title={isExpanded ? 'Recolher' : 'Expandir'}
                      >
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                      </button>
                    )}
                  </div>

                  {isExpanded && !sidebarCollapsed && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.submenus.map((sub) => {
                        const SubIcon = sub.icon;
                        const isSubActive = activeTab === item.id && activeSubTab === sub.id;
                        return (
                          <button
                            key={sub.id}
                            onClick={() => {
                              setActiveTab(item.id);
                              setActiveSubTab(sub.id === 'novo' ? 'dashboard' : sub.id);
                              if (sub.id === 'novo') setShowNewTicketDialog(true);
                            }}
                            className={`group flex items-center w-full px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${isSubActive ? 'bg-white/10 text-white shadow-lg backdrop-blur-sm border-l-2 border-[#00D4CB]' : 'text-white/50 hover:bg-white/10 hover:text-white hover:shadow-md'}`}
                          >
                            <SubIcon className="h-4 w-4 mr-3 transition-transform group-hover:scale-110" />
                            <span className="truncate">{sub.name}</span>
                            {isSubActive && <div className="ml-auto w-1.5 h-1.5 bg-[#00D4CB] rounded-full"></div>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`group w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${isActive ? 'bg-white/10 text-white shadow-lg backdrop-blur-sm ring-1 ring-white/10' : 'text-white/60 hover:bg-white/10 hover:text-white hover:shadow-md'}`}
                title={item.name}
              >
                <Icon className={`h-5 w-5 ${sidebarCollapsed ? '' : 'mr-3'} transition-transform group-hover:scale-110`} />
                {!sidebarCollapsed && (
                  <>
                    <span className="truncate flex-1 text-left">{item.name}</span>
                    {isActive && <div className="w-1.5 h-1.5 bg-[#00D4CB] rounded-full"></div>}
                  </>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="border-t border-white/10 p-4 flex-shrink-0">
        <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} mb-3`}>
          <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="h-4 w-4 text-white" />
          </div>
          {!sidebarCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-white/50 truncate">{user?.email}</p>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={logout}
          className={`w-full text-white/60 hover:bg-red-500 hover:text-white transition-all duration-200 ${sidebarCollapsed ? 'px-2' : 'justify-start'}`}
          title="Sair"
        >
          <LogOut className={`h-4 w-4 ${sidebarCollapsed ? '' : 'mr-2'}`} />
          {!sidebarCollapsed && <span>Sair</span>}
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;
